import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useSitioEntrega } from '../useSitioEntrega';
import { SitioEntrega, SitioEntregaSugerida } from '../types';

interface SitioEntregaBlockProps {
  bolsaId: string;
  entrega: SitioEntrega;
  entregaSugerida: SitioEntregaSugerida;
  soloLectura: boolean;
  // SPEC-171 — cuando es true neutraliza el mt superior para top-alinear dentro de la fila
  // entrega/validez; el contenedor grid aporta su propia separación superior.
  sinMargenSuperior?: boolean;
}

// SPEC-159 (TASK-013) — bloque inline "Sitio de entrega" en el encabezado del Detalle.
// Departamento/ciudad en cascada (patrón ClienteForm). v1.1 (RN-011): sin dirección.
// Prellena desde la sugerencia del cliente cuando no está especificada, auto-guarda al
// cambio y se muestra como texto plano "Ciudad (Departamento)" en solo lectura (RN-009).
function SitioEntregaBlock({ bolsaId, entrega, entregaSugerida, soloLectura, sinMargenSuperior = false }: SitioEntregaBlockProps) {
  const {
    departamentoId,
    ciudadId,
    departamentoNombre,
    ciudadNombre,
    departamentos,
    ciudades,
    guardando,
    error,
    cambiarDepartamento,
    cambiarCiudad,
  } = useSitioEntrega({ bolsaId, entrega, entregaSugerida, soloLectura });

  const tituloSx = { fontSize: 13, fontWeight: 700, color: '#7C3E8E', letterSpacing: 0.3 } as const;
  const mtSuperior = sinMargenSuperior ? 0 : 1.5;

  if (soloLectura) {
    const ciudad = ciudadNombre && ciudadNombre.trim() ? ciudadNombre.trim() : '';
    const depto = departamentoNombre ? ` (${departamentoNombre})` : '';
    const resumen = `${ciudad}${depto}`.trim();
    return (
      <Box
        data-testid="sitio-entrega-block"
        sx={{ mt: mtSuperior, p: 1.5, background: '#FBF7FC', border: '1px solid #EADAF0', borderRadius: '10px' }}
      >
        <Typography sx={tituloSx}>Sitio de entrega</Typography>
        <Typography data-testid="sitio-entrega-solo-lectura" sx={{ fontSize: 15, color: '#3B3542', mt: 0.5 }}>
          {resumen || 'Sin sitio de entrega'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="sitio-entrega-block"
      sx={{ mt: mtSuperior, p: 1.5, background: '#FBF7FC', border: '1px solid #EADAF0', borderRadius: '10px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={tituloSx}>Sitio de entrega</Typography>
        {guardando && (
          <Typography data-testid="sitio-entrega-guardando" sx={{ fontSize: 12, color: '#9A8FA3' }}>
            Guardando…
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <FormControl size="small" fullWidth>
          <InputLabel>Departamento</InputLabel>
          <Select
            label="Departamento"
            value={departamentoId != null ? String(departamentoId) : ''}
            onChange={(e: SelectChangeEvent) =>
              cambiarDepartamento(e.target.value ? Number(e.target.value) : null)
            }
            data-testid="entrega-departamento-select"
          >
            {departamentos.map((d) => (
              <MenuItem key={d.id} value={String(d.id)}>
                {d.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth disabled={departamentoId == null}>
          <InputLabel>Ciudad</InputLabel>
          <Select
            label="Ciudad"
            value={ciudadId != null ? String(ciudadId) : ''}
            onChange={(e: SelectChangeEvent) => cambiarCiudad(e.target.value ? Number(e.target.value) : null)}
            data-testid="entrega-ciudad-select"
          >
            {ciudades.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Typography data-testid="entrega-error" sx={{ fontSize: 13, color: '#E03030', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default SitioEntregaBlock;
